import React, { useRef, useState } from "react";
import "./FileUpload.css";

interface FileData {
  filename: string;
  content: string;
  size: number;
  type: string;
}

interface FileUploadProps {
  onFilesSelected: (files: FileData[]) => void;
  disabled?: boolean;
  maxFiles?: number;
  maxSizePerFile?: number;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFilesSelected,
  disabled = false,
  maxFiles = 20,
  maxSizePerFile = 5 * 1024 * 1024, // 5MB
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allowedTypes = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    "application/msword", // .doc
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/bmp",
    "image/tiff",
    "image/gif",
  ];

  // Fallback based on file extension, in case the browser doesn't set a useful MIME type
  const allowedExtensions = [
    ".pdf",
    ".doc",
    ".docx",
    ".jpg",
    ".jpeg",
    ".png",
    ".bmp",
    ".tiff",
    ".tif",
    ".gif",
  ];

  const validateFiles = (files: FileList): string | null => {
    if (files.length > maxFiles) {
      return `Too many files selected. Maximum ${maxFiles} files allowed.`;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > maxSizePerFile) {
        return `File "${file.name}" is too large. Maximum size is ${
          maxSizePerFile / 1024 / 1024
        }MB.`;
      }

      const extension = "." + (file.name.split(".").pop() || "").toLowerCase();
      const mimeAllowed = !!file.type && allowedTypes.includes(file.type);
      const extAllowed = allowedExtensions.includes(extension);

      if (!mimeAllowed && !extAllowed) {
        return `File "${file.name}" has an unsupported format. Allowed: PDF, Word (DOC/DOCX), JPG, PNG, BMP, TIFF, GIF.`;
      }
    }

    return null;
  };

  const processFiles = async (files: FileList): Promise<FileData[]> => {
    const processedFiles: FileData[] = [];
    const errors: string[] = [];

    // Convert FileList to Array to avoid potential issues
    const fileArray = Array.from(files);
    const totalFiles = fileArray.length;

    console.log(`Starting to process ${totalFiles} files sequentially...`);
    console.log(
      "File details:",
      fileArray.map((f) => ({ name: f.name, size: f.size, type: f.type }))
    );

    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      console.log(
        `Processing file ${i + 1}/${totalFiles}: ${file.name} (${
          file.size
        } bytes, ${file.type})`
      );

      try {
        const content = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = (e) => {
            console.log(`FileReader onload event for ${file.name}`);
            if (typeof reader.result === "string") {
              // Remove data URL prefix and get base64 content
              const base64 = reader.result.split(",")[1];
              console.log(
                `Successfully read ${file.name}, base64 length: ${base64.length}`
              );
              resolve(base64);
            } else {
              console.error(
                `FileReader result is not string for ${file.name}:`,
                typeof reader.result
              );
              reject(new Error("Failed to read file as base64"));
            }
          };

          reader.onerror = (e) => {
            console.error(`FileReader error for ${file.name}:`, reader.error);
            reject(reader.error);
          };

          console.log(`Starting FileReader.readAsDataURL for ${file.name}`);
          reader.readAsDataURL(file);
        });

        processedFiles.push({
          filename: file.name,
          content,
          size: file.size,
          type: file.type,
        });
        console.log(
          `✅ Successfully processed file ${i + 1}/${totalFiles}: ${file.name}`
        );
      } catch (error) {
        console.error(`❌ Error processing file ${file.name}:`, error);
        errors.push(
          `${file.name}: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
        // Continue processing other files instead of throwing
      }
    }

    console.log(
      `Finished processing. Success: ${processedFiles.length}, Errors: ${errors.length}`
    );

    // If some files failed, show a warning but return the successfully processed files
    if (errors.length > 0) {
      console.warn("Some files failed to process:", errors);
    }

    return processedFiles;
  };

  const handleFiles = async (files: FileList) => {
    if (!files || files.length === 0 || disabled) return;

    setError("");
    console.log(
      `FileUpload: Processing ${files.length} files:`,
      Array.from(files).map((f) => f.name)
    );

    const validationError = validateFiles(files);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsProcessing(true);
      const processedFiles = await processFiles(files);
      console.log(
        `FileUpload: Successfully processed ${processedFiles.length} files:`,
        processedFiles.map((f) => f.filename)
      );
      onFilesSelected(processedFiles);
    } catch (error) {
      console.error("FileUpload: Error processing files:", error);
      setError(
        error instanceof Error ? error.message : "Failed to process files"
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    handleFiles(files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      handleFiles(files);
    }
    // Reset input to allow selecting the same files again
    e.target.value = "";
  };

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="file-upload-container">
      <button
        type="button"
        className={`attachment-button ${disabled ? "disabled" : ""} ${
          isProcessing ? "processing" : ""
        }`}
        onClick={openFileDialog}
        disabled={disabled || isProcessing}
        title={`Attach files (PDF, Word, JPG, PNG, BMP, TIFF, GIF • Max ${maxFiles} files • ${
          maxSizePerFile / 1024 / 1024
        }MB per file)`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.bmp,.tiff,.tif,.gif"
          onChange={handleFileInputChange}
          disabled={disabled}
          style={{ display: "none" }}
        />

        {isProcessing ? (
          <div className="button-content">
            <div className="upload-spinner"></div>
          </div>
        ) : (
          <div className="button-content">
            <div className="attachment-icon">📎</div>
          </div>
        )}
      </button>

      {error && <div className="file-upload-error">{error}</div>}
    </div>
  );
};

export default FileUpload;
